import { useState } from 'react';
import type { Photo } from 'app/types/commonTypes.type';
import type { ListingVideo } from 'app/types/listingDetails.type';
import { buildResponsiveImageAttrs } from 'app/shared/utils/fedPhotos';
import { VideoLightbox } from './VideoLightbox';
import * as styles from './mediaCenter.styles';

type VideosTabProps = {
  videos: Array<ListingVideo>;
  photos?: Array<Photo>;
  previewPhoto?: Photo;
};

const PlayIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M8 5v14l11-7L8 5z" fill="white" />
  </svg>
);

const VideoPreviewCard = ({
  video,
  photo,
  previewPhoto,
  onClick,
  index,
  total,
}: {
  video: ListingVideo;
  photo?: Photo;
  previewPhoto?: Photo;
  onClick: () => void;
  index: number;
  total: number;
}) => {
  const displayPhoto = photo || previewPhoto;
  const previewUrl = displayPhoto?.url;
  const imageAttrs = previewUrl ? buildResponsiveImageAttrs(previewUrl, 768, [384, 768, 1152]) : null;
  const label = video.label || (total > 1 ? `Play video ${index + 1} of ${total}` : 'Play video');

  return (
    <button className={styles.tourButton} onClick={onClick} aria-label={label}>
      <div className={styles.tourPreview}>
        {imageAttrs ? (
          <img
            src={imageAttrs.src}
            srcSet={imageAttrs.srcSet}
            sizes="100vw"
            alt={`Video ${index + 1} preview`}
            className={styles.tourPreviewImage}
          />
        ) : previewUrl ? (
          <img src={previewUrl} alt={`Video ${index + 1} preview`} className={styles.tourPreviewImage} />
        ) : null}
        <div className={styles.tourOverlay}>
          <div className={styles.tourIconCircle}>
            <PlayIcon />
          </div>
          <span className={styles.tourLabel}>{label}</span>
        </div>
      </div>
    </button>
  );
};

export const VideosTab = ({ videos, photos = [], previewPhoto }: VideosTabProps) => {
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);

  if (videos.length === 0) {
    return (
      <div role="tabpanel" id="tabpanel-videos" aria-labelledby="tab-videos">
        <p className={styles.emptyState}>No videos available for this listing.</p>
      </div>
    );
  }

  return (
    <div role="tabpanel" id="tabpanel-videos" aria-labelledby="tab-videos" className={styles.tourContainer}>
      {videos.length === 1 ? (
        <VideoPreviewCard
          video={videos[0]}
          photo={photos[0]}
          previewPhoto={previewPhoto}
          onClick={() => setActiveVideoIndex(0)}
          index={0}
          total={1}
        />
      ) : (
        <div className={styles.tourList}>
          {videos.map((video, i) => (
            <VideoPreviewCard
              key={i}
              video={video}
              photo={photos[i]}
              previewPhoto={previewPhoto}
              onClick={() => setActiveVideoIndex(i)}
              index={i}
              total={videos.length}
            />
          ))}
        </div>
      )}

      {activeVideoIndex !== null && (
        <VideoLightbox
          videoUrl={videos[activeVideoIndex].url}
          open={true}
          onOpenChange={(open) => {
            if (!open) setActiveVideoIndex(null);
          }}
        />
      )}
    </div>
  );
};
